import { useEffect, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { Activity, RotateCcw, SlidersHorizontal, TrendingUp } from "lucide-react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { computePitcherPoints } from "@convex/_scoring/computePitcherPoints";
import { computePoints } from "@convex/_scoring/computePoints";
import type { PitchingRulesetPoints, RulesetPoints } from "@convex/_scoring/rulesets";
import { DEFAULT_PLAYER_TYPE, type PlayerType } from "@convex/_lib/playerTypes";
import { useMyLeagues } from "@/features/leagues/hooks/useLeague";
import { Button } from "@/shared/ui/Button";
import { Skeleton } from "@/shared/ui/Skeleton";

export const Route = createFileRoute("/_authenticated/PlayWithTracker")({
  component: PlayWithTrackerRoute,
});

const HITTER_FIELDS: { key: keyof RulesetPoints; label: string }[] = [
  { key: "single", label: "1B" },
  { key: "double", label: "2B" },
  { key: "triple", label: "3B" },
  { key: "homeRun", label: "HR" },
  { key: "rbi", label: "RBI" },
  { key: "run", label: "R" },
  { key: "walk", label: "BB" },
  { key: "stolenBase", label: "SB" },
  { key: "caughtStealing", label: "CS" },
  { key: "strikeout", label: "K" },
];

const PITCHER_FIELDS: { key: keyof PitchingRulesetPoints; label: string }[] = [
  { key: "perOut", label: "Out" },
  { key: "perStrikeout", label: "K" },
  { key: "perEarnedRun", label: "ER" },
  { key: "perWalk", label: "BB" },
  { key: "perHitAllowed", label: "H" },
  { key: "perWin", label: "W" },
  { key: "perLoss", label: "L" },
  { key: "perSave", label: "SV" },
  { key: "perCompleteGame", label: "CG" },
  { key: "perShutout", label: "SHO" },
];

type SimRules = {
  hitter: RulesetPoints;
  pitcher: PitchingRulesetPoints;
};

function PlayWithTrackerRoute() {
  const myLeagues = useMyLeagues();
  const [leagueId, setLeagueId] = useState<string>("");
  const dataset = useQuery(
    api.playWithTracker.getLeagueSimulationDataset,
    leagueId ? { leagueId: leagueId as Id<"leagues"> } : "skip",
  );
  const [rules, setRules] = useState<SimRules | null>(null);
  const [rulesSource, setRulesSource] = useState<string>("");

  useEffect(() => {
    if (!leagueId && myLeagues && myLeagues.length > 0) {
      const firstLeague = myLeagues[0];
      if (firstLeague) setLeagueId(firstLeague.league._id);
    }
  }, [leagueId, myLeagues]);

  useEffect(() => {
    if (!dataset) return;
    const source = dataset.league._id;
    if (rulesSource === source) return;
    setRules(dataset.liveRules);
    setRulesSource(source);
  }, [dataset, rulesSource]);

  const simulation = useMemo(() => {
    if (!dataset || !rules) return null;
    return simulate(dataset, rules);
  }, [dataset, rules]);

  if (myLeagues === undefined || (leagueId && dataset === undefined)) {
    return <TrackerLoading />;
  }

  if (!myLeagues || myLeagues.length === 0) {
    return (
      <section className="pt-4">
        <TrackerHeader />
        <div className="border border-border bg-surface p-5 text-sm text-muted">
          Join or create a league before using the tracker.
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-6 pt-4">
      <TrackerHeader />

      <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
        <label className="flex flex-col gap-2 text-xs font-mono uppercase tracking-[0.16em] text-muted">
          League
          <select
            value={leagueId}
            onChange={(event) => {
              setLeagueId(event.target.value);
              setRulesSource("");
            }}
            className="h-12 rounded-sm border border-border bg-surface px-4 text-sm font-sans normal-case tracking-normal text-ink outline-none transition-colors hover:border-border-strong focus:border-ink focus:ring-2 focus:ring-accent/30"
          >
            {myLeagues.map(({ league }) => (
              <option key={league._id} value={league._id}>
                {league.name} ({league.season})
              </option>
            ))}
          </select>
        </label>
        <Button
          type="button"
          variant="secondary"
          onClick={() => dataset && setRules(dataset.liveRules)}
          disabled={!dataset}
        >
          <RotateCcw className="h-4 w-4" />
          Live rules
        </Button>
      </div>

      {dataset && rules && simulation ? (
        <>
          <PresetBar dataset={dataset} setRules={setRules} />
          <SummaryStrip simulation={simulation} />
          <RulesEditor rules={rules} setRules={setRules} />
          <StandingsTable rows={simulation.standings} />
          <PlayerImpactTable rows={simulation.players} />
        </>
      ) : null}
    </section>
  );
}

function TrackerHeader() {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-label text-accent">Temporary lab</p>
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <h1 className="font-display text-[42px] leading-none uppercase md:text-[58px]">
          Play With Tracker
        </h1>
        <p className="max-w-xl text-sm leading-6 text-muted">
          Change hitter and pitcher weights locally, then compare the simulated standings against
          the current stored totals. Nothing here writes to Convex.
        </p>
      </div>
    </div>
  );
}

function TrackerLoading() {
  return (
    <section className="flex flex-col gap-5 pt-4">
      <TrackerHeader />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-72 w-full" />
    </section>
  );
}

function PresetBar({
  dataset,
  setRules,
}: {
  dataset: TrackerDataset;
  setRules: (rules: SimRules) => void;
}) {
  const live = dataset.liveRules;
  return (
    <div className="grid gap-3 md:grid-cols-4">
      <PresetButton
        label="Try recommended"
        detail="Hitters x1.5, pitcher bonuses tamed"
        onClick={() =>
          setRules({
            hitter: scaleRules(live.hitter, 1.5),
            pitcher: tamePitcherBonuses(live.pitcher),
          })
        }
      />
      <PresetButton
        label="Hitter lift"
        detail="Hitters x1.6, pitchers x0.9"
        onClick={() =>
          setRules({ hitter: scaleRules(live.hitter, 1.6), pitcher: scaleRules(live.pitcher, 0.9) })
        }
      />
      <PresetButton
        label="Tame bonuses"
        detail="Wins, saves, CG, SHO -50%"
        onClick={() =>
          setRules({
            hitter: live.hitter,
            pitcher: tamePitcherBonuses(live.pitcher),
          })
        }
      />
      <PresetButton
        label="Double hitters"
        detail="Hitter events x2"
        onClick={() => setRules({ hitter: scaleRules(live.hitter, 2), pitcher: live.pitcher })}
      />
    </div>
  );
}

function PresetButton({
  label,
  detail,
  onClick,
}: {
  label: string;
  detail: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-20 flex-col items-start justify-center gap-1 rounded-sm border border-border bg-surface px-4 text-left transition-colors hover:border-accent/70 hover:bg-surface-2"
    >
      <span className="text-sm font-semibold text-ink">{label}</span>
      <span className="text-xs text-muted">{detail}</span>
    </button>
  );
}

function SummaryStrip({ simulation }: { simulation: Simulation }) {
  const top = simulation.standings[0];
  const second = simulation.standings[1];
  const spread = top && second ? top.simulatedTotal - second.simulatedTotal : 0;
  return (
    <div className="grid gap-3 md:grid-cols-4">
      <MetricCard icon={<TrendingUp className="h-4 w-4" />} label="Leader" value={top?.teamName ?? "—"} />
      <MetricCard label="Lead over 2nd" value={formatPoints(spread)} />
      <MetricCard label="Hitter share" value={`${Math.round(simulation.hitterShare * 100)}%`} />
      <MetricCard label="Pitcher share" value={`${Math.round(simulation.pitcherShare * 100)}%`} />
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon?: ReactNode;
}) {
  return (
    <div className="rounded-sm border border-border bg-surface p-4">
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-muted">
        {icon}
        {label}
      </div>
      <div className="mt-2 truncate text-2xl font-semibold tabular-nums text-ink">{value}</div>
    </div>
  );
}

function RulesEditor({
  rules,
  setRules,
}: {
  rules: SimRules;
  setRules: Dispatch<SetStateAction<SimRules | null>>;
}) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <RulePanel
        title="Hitter points"
        fields={HITTER_FIELDS}
        values={rules.hitter}
        onChange={(key, value) =>
          setRules((current) =>
            current ? { ...current, hitter: { ...current.hitter, [key]: value } } : current,
          )
        }
      />
      <RulePanel
        title="Pitcher points"
        fields={PITCHER_FIELDS}
        values={rules.pitcher}
        onChange={(key, value) =>
          setRules((current) =>
            current ? { ...current, pitcher: { ...current.pitcher, [key]: value } } : current,
          )
        }
      />
    </div>
  );
}

function RulePanel<T extends Record<string, number>>({
  title,
  fields,
  values,
  onChange,
}: {
  title: string;
  fields: { key: keyof T; label: string }[];
  values: T;
  onChange: (key: keyof T, value: number) => void;
}) {
  return (
    <div className="rounded-sm border border-border bg-surface p-4">
      <div className="mb-4 flex items-center gap-2">
        <SlidersHorizontal className="h-4 w-4 text-accent" />
        <h2 className="text-base font-semibold text-ink">{title}</h2>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {fields.map((field) => (
          <label key={String(field.key)} className="flex flex-col gap-1.5">
            <span className="text-xs font-mono uppercase tracking-[0.14em] text-muted">
              {field.label}
            </span>
            <input
              type="number"
              step="0.5"
              value={values[field.key]}
              onChange={(event) => onChange(field.key, Number(event.target.value))}
              className="h-11 rounded-sm border border-border bg-bg px-3 text-sm tabular-nums text-ink outline-none transition-colors hover:border-border-strong focus:border-ink focus:ring-2 focus:ring-accent/30"
            />
          </label>
        ))}
      </div>
    </div>
  );
}

function StandingsTable({ rows }: { rows: Simulation["standings"] }) {
  return (
    <TableShell title="League impact">
      <thead>
        <tr className="text-left text-xs font-mono uppercase tracking-[0.14em] text-muted">
          <th className="py-3 pr-3">Rank</th>
          <th className="py-3 pr-3">Team</th>
          <th className="py-3 pr-3 text-right">Current</th>
          <th className="py-3 pr-3 text-right">Sim</th>
          <th className="py-3 pr-3 text-right">Δ</th>
          <th className="py-3 pr-3 text-right">Hitters</th>
          <th className="py-3 text-right">Pitchers</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={row.memberId} className="border-t border-border/70 text-sm">
            <td className="py-3 pr-3 font-mono text-muted">{index + 1}</td>
            <td className="py-3 pr-3 font-semibold text-ink">{row.teamName}</td>
            <td className="py-3 pr-3 text-right tabular-nums">{formatPoints(row.currentTotal)}</td>
            <td className="py-3 pr-3 text-right font-semibold tabular-nums text-ink">
              {formatPoints(row.simulatedTotal)}
            </td>
            <td className="py-3 pr-3 text-right tabular-nums">{formatSigned(row.delta)}</td>
            <td className="py-3 pr-3 text-right tabular-nums">{formatPoints(row.hitterPoints)}</td>
            <td className="py-3 text-right tabular-nums">{formatPoints(row.pitcherPoints)}</td>
          </tr>
        ))}
      </tbody>
    </TableShell>
  );
}

function PlayerImpactTable({ rows }: { rows: Simulation["players"] }) {
  return (
    <TableShell title="Player impact">
      <thead>
        <tr className="text-left text-xs font-mono uppercase tracking-[0.14em] text-muted">
          <th className="py-3 pr-3">Player</th>
          <th className="py-3 pr-3">Team</th>
          <th className="py-3 pr-3">Kind</th>
          <th className="py-3 pr-3 text-right">Current</th>
          <th className="py-3 pr-3 text-right">Sim</th>
          <th className="py-3 text-right">Δ</th>
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, 25).map((row) => (
          <tr key={row.playerId} className="border-t border-border/70 text-sm">
            <td className="py-3 pr-3 font-semibold text-ink">{row.fullName}</td>
            <td className="py-3 pr-3 text-muted">{row.teamShortName}</td>
            <td className="py-3 pr-3 text-muted">{row.kind}</td>
            <td className="py-3 pr-3 text-right tabular-nums">{formatPoints(row.currentTotal)}</td>
            <td className="py-3 pr-3 text-right font-semibold tabular-nums text-ink">
              {formatPoints(row.simulatedTotal)}
            </td>
            <td className="py-3 text-right tabular-nums">{formatSigned(row.delta)}</td>
          </tr>
        ))}
      </tbody>
    </TableShell>
  );
}

function TableShell({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-sm border border-border bg-surface">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <Activity className="h-4 w-4 text-accent" />
        <h2 className="text-base font-semibold text-ink">{title}</h2>
      </div>
      <div className="overflow-x-auto px-4">
        <table className="w-full min-w-[720px] border-collapse">{children}</table>
      </div>
    </div>
  );
}

type Simulation = ReturnType<typeof simulate>;

type TrackerDataset = {
  league: {
    _id: Id<"leagues">;
    name: string;
    season: number;
    rosterSize: number;
    hitterSlots: number;
    pitcherSlots: number;
  };
  members: Array<{ _id: Id<"leagueMembers">; teamName: string }>;
  roster: Array<{
    memberId: Id<"leagueMembers">;
    playerId: Id<"players">;
    fullName: string;
    position: PlayerType;
    pickNumber: number;
    slot: "H" | "P";
    teamShortName: string;
  }>;
  hitterLines: Array<{
    memberId: Id<"leagueMembers">;
    playerId: Id<"players">;
    gameId: Id<"games">;
    gameDate: number;
    currentPoints: number;
    hits: number;
    doubles: number;
    triples: number;
    homeRuns: number;
    rbi: number;
    runs: number;
    walks: number;
    stolenBases: number;
    caughtStealing: number;
    strikeouts: number;
  }>;
  pitcherLines: Array<{
    memberId: Id<"leagueMembers">;
    playerId: Id<"players">;
    gameId: Id<"games">;
    gameDate: number;
    currentPoints: number;
    inningsPitchedOuts: number;
    hits: number;
    runs: number;
    earnedRuns: number;
    walks: number;
    strikeouts: number;
    decision: "W" | "L" | "S" | undefined;
    completeGame: boolean;
    shutout: boolean;
  }>;
  liveRules: SimRules;
};

function simulate(dataset: TrackerDataset, rules: SimRules) {
  const memberNames = new Map(dataset.members.map((member) => [member._id, member.teamName]));
  const playerMeta = new Map(dataset.roster.map((player) => [player.playerId, player]));
  const byMember = new Map<
    string,
    {
      memberId: string;
      teamName: string;
      currentTotal: number;
      simulatedTotal: number;
      hitterPoints: number;
      pitcherPoints: number;
    }
  >();
  const byPlayer = new Map<
    string,
    {
      playerId: string;
      fullName: string;
      teamShortName: string;
      kind: string;
      currentTotal: number;
      simulatedTotal: number;
    }
  >();

  function memberRow(memberId: string) {
    let row = byMember.get(memberId);
    if (!row) {
      row = {
        memberId,
        teamName: memberNames.get(memberId as Id<"leagueMembers">) ?? "(unknown)",
        currentTotal: 0,
        simulatedTotal: 0,
        hitterPoints: 0,
        pitcherPoints: 0,
      };
      byMember.set(memberId, row);
    }
    return row;
  }

  function playerRow(playerId: string) {
    const meta = playerMeta.get(playerId as Id<"players">);
    let row = byPlayer.get(playerId);
    if (!row) {
      row = {
        playerId,
        fullName: meta?.fullName ?? "(unknown)",
        teamShortName: meta?.teamShortName ?? "",
        kind: meta?.position ?? DEFAULT_PLAYER_TYPE,
        currentTotal: 0,
        simulatedTotal: 0,
      };
      byPlayer.set(playerId, row);
    }
    return row;
  }

  for (const member of dataset.members) memberRow(member._id);

  let hitterTotal = 0;
  let pitcherTotal = 0;

  for (const line of dataset.hitterLines) {
    const simulated = computePoints(line, rules.hitter).fantasyPoints;
    const member = memberRow(line.memberId);
    const player = playerRow(line.playerId);
    member.currentTotal += line.currentPoints;
    member.simulatedTotal += simulated;
    member.hitterPoints += simulated;
    player.currentTotal += line.currentPoints;
    player.simulatedTotal += simulated;
    hitterTotal += simulated;
  }

  for (const line of dataset.pitcherLines) {
    const pitcherLine = {
      inningsPitchedOuts: line.inningsPitchedOuts,
      hits: line.hits,
      earnedRuns: line.earnedRuns,
      walks: line.walks,
      strikeouts: line.strikeouts,
      ...(line.decision === undefined ? {} : { decision: line.decision }),
    };
    const simulated = computePitcherPoints(rules.pitcher, pitcherLine, {
      completeGame: line.completeGame,
      shutout: line.shutout,
    }).fantasyPoints;
    const member = memberRow(line.memberId);
    const player = playerRow(line.playerId);
    member.currentTotal += line.currentPoints;
    member.simulatedTotal += simulated;
    member.pitcherPoints += simulated;
    player.currentTotal += line.currentPoints;
    player.simulatedTotal += simulated;
    pitcherTotal += simulated;
  }

  const standings = [...byMember.values()]
    .map((row) => ({ ...row, delta: row.simulatedTotal - row.currentTotal }))
    .sort((a, b) => b.simulatedTotal - a.simulatedTotal || a.teamName.localeCompare(b.teamName));

  const players = [...byPlayer.values()]
    .map((row) => ({ ...row, delta: row.simulatedTotal - row.currentTotal }))
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  const total = hitterTotal + pitcherTotal;
  return {
    standings,
    players,
    hitterShare: total === 0 ? 0 : hitterTotal / total,
    pitcherShare: total === 0 ? 0 : pitcherTotal / total,
  };
}

function scaleRules<T extends Record<string, number>>(rules: T, factor: number): T {
  return Object.fromEntries(
    Object.entries(rules).map(([key, value]) => [key, Math.round(value * factor * 100) / 100]),
  ) as T;
}

function tamePitcherBonuses(points: PitchingRulesetPoints): PitchingRulesetPoints {
  return {
    ...points,
    perWin: 3,
    perLoss: -3,
    perSave: 3,
    perCompleteGame: 4,
    perShutout: 3,
  };
}

function formatPoints(value: number) {
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 1,
    minimumFractionDigits: Number.isInteger(value) ? 0 : 1,
  }).format(value);
}

function formatSigned(value: number) {
  if (value === 0) return "0";
  return `${value > 0 ? "+" : ""}${formatPoints(value)}`;
}
